/**
 * 3 時間ルール (集荷 → 配達) の「ものさし」。api (recalc・保存ゲート) と admin (バナー表示) が
 * 同じ定義を使うための単一源泉 (#695)。
 *
 * ## 定義: 3 時間は「作業開始時刻の差」で測る (現地待機を含む)
 *
 * `配達の作業開始 − 集荷の作業開始 ≤ system_settings.max_ride_minutes`。
 * optimizer の `Time` ディメンション (`CumulVar(delivery) − CumulVar(pickup) ≤ 上限`) と同じ定義で、
 * OR-Tools の `CumulVar` は時間窓待機 (slack) を含んだ**作業開始時刻**を表す。
 *
 * **物理到着差で測ってはいけない**: 「13:28 に集荷 → 14:00 に配達先へ着き、指定 17:30 まで現地待機」
 * は物理到着差 32 分だが、荷物は 4 時間車内にある。到着差で判定すると鮮度違反を検知できない
 * (2026-08-20 現場報告「17:30 届けのピックが 13:28 になっています」= Issue #695)。
 *
 * ## 集荷側の下限 (ride time floor)
 *
 * 配達に開始指定があると配達側は必ずその時刻まで待つ (`CumulVar(d) ≥ 窓開始`) ため、
 * 上の制約から `集荷の作業開始 ≥ 配達の指定開始 − 上限` が導かれる。solver はこの下限を
 * 守った解を返すが、api の recalc は各 stop の物理到着を前から積むだけなので、
 * この下限を明示的に適用しないと solver が組んだ計画を再現できない。
 * `buildRideTimePickupFloorMs` がその下限を返し、recalc は作業開始時刻に反映する。
 *
 * ## 上限は注文ごとに違う (#767)
 *
 * 弁当は鮮度で 3 時間、ドライアイス等は 5 時間と**案件ごとに上限が決まる**。各関数は
 * 「システム設定の既定 (引数 `maxRideSeconds`)」を受け取りつつ、stop に載った
 * `max_ride_seconds` があればそちらを優先する (`resolveRideTimeMaxSeconds`)。
 * 上書きの無い注文は引数の既定に落ちるので、従来の判定と完全に同じ結果になる。
 */

/** 集荷側とみなす stop 種別 (クロスドックの積込を含む)。 */
const PICKUP_STOP_TYPES: ReadonlySet<string> = new Set(["pickup", "cross_dock_pickup"]);

/** 配達側とみなす stop 種別 (クロスドックの荷降ろしを含む)。 */
const DELIVERY_STOP_TYPES: ReadonlySet<string> = new Set(["delivery", "cross_dock_drop"]);

export function isRideTimePickupStop(stopType: string): boolean {
  return PICKUP_STOP_TYPES.has(stopType);
}

/**
 * 3 時間ルールの**起点になりうる** stop か (= そこで荷物を積むか)。#724
 *
 * 集荷のほか、積込を伴う経由地 (`waypoint_action='load'`) も起点になる。
 * パターンB (自社倉庫で積込 → 取引先の集荷先 → 納品) では倉庫が起点で、集荷から測ると
 * 倉庫からの経過時間が抜け落ちる。optimizer 側 (`solver._ride_time_origin_node`) と同じ規則。
 * 積込を伴わない経由地 (降ろす / 立ち寄るだけ) は起点にも配達側にもしない。
 */
export function isRideTimeLoadingStop(stop: {
  stop_type: string;
  waypoint_action?: string | null;
}): boolean {
  if (PICKUP_STOP_TYPES.has(stop.stop_type)) return true;
  return stop.stop_type === "waypoint" && stop.waypoint_action === "load";
}

export function isRideTimeDeliveryStop(stopType: string): boolean {
  return DELIVERY_STOP_TYPES.has(stopType);
}

/**
 * その stop に適用する上限 (秒) を返す (#767)。
 * 注文ごとの上書きが無ければ既定 (`system_settings.max_ride_minutes` 由来) を使う。
 * 0 以下・非有限の値は既定に落とす (上限 0 で全注文が違反になるのを防ぐ)。
 */
export function resolveRideTimeMaxSeconds(
  stop: { max_ride_seconds?: number | null },
  defaultMaxRideSeconds: number,
): number {
  const override = stop.max_ride_seconds;
  if (override == null || !Number.isFinite(override) || override <= 0) return defaultMaxRideSeconds;
  return override;
}

/** "HH:MM" / "HH:MM:SS" (JST) を当日 0 時からの秒数にする。解釈できなければ null。 */
export function rideTimeClockSeconds(clock: string | null | undefined): number | null {
  if (!clock) return null;
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(clock.trim());
  if (!m) return null;
  const hours = Number(m[1]);
  const minutes = Number(m[2]);
  const seconds = m[3] !== undefined ? Number(m[3]) : 0;
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  return hours * 3600 + minutes * 60 + seconds;
}

/** 配送日 (YYYY-MM-DD) と "HH:MM" (JST) から絶対時刻 (ms) を作る。解釈できなければ null。 */
export function jstClockMs(deliveryDate: string, clock: string | null | undefined): number | null {
  const seconds = rideTimeClockSeconds(clock);
  if (seconds == null) return null;
  const midnight = Date.parse(`${deliveryDate}T00:00:00+09:00`);
  if (Number.isNaN(midnight)) return null;
  return midnight + seconds * 1000;
}

/** 集荷下限の算出に必要な最小フィールド。`sequence` 昇順で渡すこと。 */
export interface RideTimePairStop {
  /** route_stops の public_id */
  id: string;
  /**
   * この stop が属する注文だけの上限 (秒)。null / undefined は引数の既定
   * (`system_settings.max_ride_minutes` 由来) を使う (#767)。
   *
   * 上限は**注文ごと**に違う (弁当 3 時間 / ドライアイス 5 時間)。同じ注文の集荷・配達には
   * 同じ値が載る前提で、判定は配達側 stop の値で行う (差を測るのが配達側のため)。
   */
  max_ride_seconds?: number | null;
  /** "pickup" | "delivery" | "waypoint" | "cross_dock_pickup" | "cross_dock_drop" など */
  stop_type: string;
  /**
   * 経由地 (`stop_type='waypoint'`) での作業種別 ("load" | "unload" | "none")。#724
   * `load` の経由地だけが 3 時間ルールの起点になりうる。経由地以外では null。
   */
  waypoint_action?: string | null;
  /** 紐付く注文の public_id。null の stop はペアを作らない */
  order_id?: string | null;
  /** 注文の指定配達開始 (HH:MM, JST)。delivery 系 stop でのみ意味を持つ */
  delivery_time_start?: string | null;
}

/**
 * 集荷 stop ごとの「作業開始の下限」(絶対時刻 ms) を求める。
 *
 * 同じ注文の配達に開始指定があるとき、その集荷は `配達指定開始 − 上限` より前に始められない。
 * 下限が無い集荷 (配達に開始指定が無い / ペアが無い) は Map に載せない。
 * 同一注文に配達 stop が複数ある場合 (クロスドック) は最も厳しい (遅い) 下限を採る。
 */
export function buildRideTimePickupFloorMs(
  deliveryDate: string,
  stops: readonly RideTimePairStop[],
  maxRideSeconds: number,
): Map<string, number> {
  const pickupIdByOrder = new Map<string, string>();
  const floors = new Map<string, number>();

  for (const stop of stops) {
    const orderId = stop.order_id;
    if (!orderId) continue;
    if (isRideTimeLoadingStop(stop)) {
      // 起点は「最初に積んだ地点」なので、後ろの積込 stop で上書きしない (#724)。
      if (!pickupIdByOrder.has(orderId)) pickupIdByOrder.set(orderId, stop.id);
      continue;
    }
    if (!isRideTimeDeliveryStop(stop.stop_type)) continue;
    const pickupId = pickupIdByOrder.get(orderId);
    if (!pickupId) continue; // 集荷が後ろにある/無い場合は別の警告 (順序違反) で表現する
    const windowStartMs = jstClockMs(deliveryDate, stop.delivery_time_start);
    if (windowStartMs == null) continue;
    const floorMs = windowStartMs - resolveRideTimeMaxSeconds(stop, maxRideSeconds) * 1000;
    const current = floors.get(pickupId);
    if (current == null || floorMs > current) floors.set(pickupId, floorMs);
  }
  return floors;
}

/** 作業開始時刻の算出に必要な最小フィールド。`sequence` 昇順で渡すこと。 */
export interface RideTimeServiceStop extends RideTimePairStop {
  /** 物理到着予測 (ISO8601 または Date)。未算出は null */
  estimated_arrival: string | Date | null | undefined;
  /** 注文の指定集荷開始 (HH:MM, JST)。pickup 系 stop でのみ意味を持つ */
  pickup_time_start?: string | null;
}

function toMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isNaN(ms) ? null : ms;
}

/**
 * 各 stop の「作業開始時刻」(ms) を求める。
 * `max(物理到着, 指定窓の開始, 集荷下限)` で、recalc が estimated_departure を積むときの基準と同じ。
 * 到着予測が無い stop は Map に載せない (判定不能)。
 */
export function buildRideTimeServiceStartMs(
  deliveryDate: string,
  stops: readonly RideTimeServiceStop[],
  maxRideSeconds: number,
): Map<string, number> {
  const floors = buildRideTimePickupFloorMs(deliveryDate, stops, maxRideSeconds);
  const serviceStarts = new Map<string, number>();

  for (const stop of stops) {
    const arrivalMs = toMs(stop.estimated_arrival);
    if (arrivalMs == null) continue;
    const pickup = isRideTimeLoadingStop(stop);
    const windowStartMs = jstClockMs(
      deliveryDate,
      pickup ? stop.pickup_time_start : stop.delivery_time_start,
    );
    let serviceStartMs = arrivalMs;
    if (windowStartMs != null && windowStartMs > serviceStartMs) serviceStartMs = windowStartMs;
    const floorMs = floors.get(stop.id);
    if (floorMs != null && floorMs > serviceStartMs) serviceStartMs = floorMs;
    serviceStarts.set(stop.id, serviceStartMs);
  }
  return serviceStarts;
}

/**
 * 作業開始差が上限を超える注文の集荷・配達 stop id を返す。
 *
 * HH:MM 丸めは使わず ms 差で判定する。集荷が無い注文 (delivery-only) は対象外。
 * 到着予測が無い stop は判定できないので飛ばす (楽観更新中に実在しない違反を出さない)。
 */
export function findRideTimeViolationStopIds(
  deliveryDate: string,
  stops: readonly RideTimeServiceStop[],
  maxRideSeconds: number,
): Set<string> {
  const serviceStarts = buildRideTimeServiceStartMs(deliveryDate, stops, maxRideSeconds);
  const pickupByOrder = new Map<string, { id: string; serviceStartMs: number }>();
  const violations = new Set<string>();

  for (const stop of stops) {
    const orderId = stop.order_id;
    if (!orderId) continue;
    const serviceStartMs = serviceStarts.get(stop.id);
    if (serviceStartMs == null) continue;

    if (isRideTimeLoadingStop(stop)) {
      // 起点は「最初に積んだ地点」。後ろの積込で上書きすると経過時間が短く見え、
      // 実際には車内にある荷物の違反を見逃す (#724)。
      if (!pickupByOrder.has(orderId)) pickupByOrder.set(orderId, { id: stop.id, serviceStartMs });
      continue;
    }
    if (!isRideTimeDeliveryStop(stop.stop_type)) continue;
    const pickup = pickupByOrder.get(orderId);
    if (!pickup) continue;
    if (
      serviceStartMs - pickup.serviceStartMs >
      resolveRideTimeMaxSeconds(stop, maxRideSeconds) * 1000
    ) {
      violations.add(pickup.id);
      violations.add(stop.id);
    }
  }
  return violations;
}
